/**
 * 수수료 집계기 파서 — 레거시 `commissionSheetParser.js` 확장 (템플릿·드라이브·범용)
 */
import * as XLSX from 'xlsx';
import {
  driveRowToPreview,
  parseDriveWorkbook,
} from '@/pages/settlement-fees/commissionDriveFormat';
import { extractTemplateFile } from '@/pages/settlement-fees/commissionTemplateRegistry';
import { normalizeCommissionDrugCode } from '@/pages/settlement-fees/commissionDrugCode';

export interface CommissionParseRow {
  referenceYearMonth: string;
  pharmaceuticalCompanyName: string;
  drugCode: string;
  drugName: string;
  price: number;
  standardCommissionRate: number;
  commissionNote: string;
  sourceFile: string;
}

export interface CommissionFileStatus {
  fileName: string;
  status: 'parsed' | 'unmatched' | 'unsupported' | 'error';
  extractedRowCount: number;
  message?: string;
}

export interface CommissionParseResult {
  summary: {
    totalFiles: number;
    parsedFiles: number;
    extractedRows: number;
    pharmaceuticalCompanies: number;
  };
  rows: CommissionParseRow[];
  files: CommissionFileStatus[];
}

const HEADER_ALIASES: Record<
  keyof Omit<CommissionParseRow, 'sourceFile'>,
  string[]
> = {
  referenceYearMonth: ['년월', '기준년월', '정산년월', 'reference_year_month'],
  pharmaceuticalCompanyName: [
    '제약사',
    '제약사명',
    'pharmaceutical_company',
    'pharma_company_name',
  ],
  drugCode: [
    '제품코드',
    '의약품코드',
    '의약품 코드',
    'drug_code',
    'drugcode',
    'edi코드',
  ],
  drugName: ['제품명', '품명', 'drug_name', '의약품명'],
  price: ['보험가', '단가', 'price', '약가'],
  standardCommissionRate: [
    '제약사수수료율',
    '수수료율',
    'standard_commission_rate',
    '정산수수료율',
  ],
  commissionNote: ['비고', '비고1', 'standard_commission_note', 'note'],
};

const normalizeHeader = (value: unknown): string =>
  String(value ?? '')
    .trim()
    .replace(/\s+/g, '')
    .toLowerCase();

const findColumnIndex = (headers: string[], aliases: string[]): number => {
  const normalized = headers.map(normalizeHeader);
  for (const alias of aliases) {
    const idx = normalized.indexOf(normalizeHeader(alias));
    if (idx >= 0) return idx;
  }
  return -1;
};

const parseNumber = (value: unknown): number => {
  if (value == null || value === '') return 0;
  const num = Number(String(value).replace(/,/g, '').replace(/%/g, ''));
  return Number.isNaN(num) ? 0 : num;
};

const normalizeDrugCode = (value: unknown): string =>
  normalizeCommissionDrugCode(value);

const extractRows = (
  sheetRows: unknown[][],
  fileName: string,
  defaultYm: string,
): CommissionParseRow[] => {
  if (sheetRows.length < 2) return [];

  let headerRowIndex = 0;
  for (let i = 0; i < Math.min(sheetRows.length, 25); i++) {
    const joined = (sheetRows[i] ?? [])
      .map((cell) => normalizeHeader(cell))
      .join('|');
    if (
      joined.includes('제품코드') ||
      joined.includes('drugcode') ||
      joined.includes('의약품코드')
    ) {
      headerRowIndex = i;
      break;
    }
  }

  const headers = (sheetRows[headerRowIndex] ?? []).map((cell) =>
    String(cell ?? ''),
  );
  const columnMap = Object.fromEntries(
    Object.entries(HEADER_ALIASES).map(([key, aliases]) => [
      key,
      findColumnIndex(headers, aliases),
    ]),
  ) as Record<keyof Omit<CommissionParseRow, 'sourceFile'>, number>;

  if (columnMap.drugCode < 0 && columnMap.drugName < 0) {
    return [];
  }

  const rows: CommissionParseRow[] = [];
  for (let i = headerRowIndex + 1; i < sheetRows.length; i++) {
    const row = sheetRows[i] ?? [];
    const drugCode =
      columnMap.drugCode >= 0
        ? normalizeDrugCode(row[columnMap.drugCode])
        : '';
    const drugName =
      columnMap.drugName >= 0
        ? String(row[columnMap.drugName] ?? '').trim()
        : '';
    if (!drugCode && !drugName) continue;

    const ymRaw =
      columnMap.referenceYearMonth >= 0
        ? String(row[columnMap.referenceYearMonth] ?? '').replace(/\D/g, '')
        : '';
    const referenceYearMonth =
      ymRaw.length === 6 ? ymRaw : defaultYm;

    rows.push({
      referenceYearMonth,
      pharmaceuticalCompanyName:
        columnMap.pharmaceuticalCompanyName >= 0
          ? String(row[columnMap.pharmaceuticalCompanyName] ?? '').trim()
          : '',
      drugCode,
      drugName,
      price: parseNumber(row[columnMap.price]),
      standardCommissionRate: parseNumber(row[columnMap.standardCommissionRate]),
      commissionNote:
        columnMap.commissionNote >= 0
          ? String(row[columnMap.commissionNote] ?? '').trim()
          : '',
      sourceFile: fileName,
    });
  }

  return rows;
};

export const parseCommissionFiles = async (
  files: File[],
  referenceYearMonth: string,
): Promise<CommissionParseResult> => {
  const sorted = [...files].sort((a, b) => a.name.localeCompare(b.name, 'ko'));
  const fileStatuses: CommissionFileStatus[] = [];
  const rows: CommissionParseRow[] = [];

  for (const file of sorted) {
    const ext = file.name.toLowerCase().match(/\.[^.]+$/)?.[0] ?? '';
    if (!['.xlsx', '.xls'].includes(ext)) {
      fileStatuses.push({
        fileName: file.name,
        status: 'unsupported',
        extractedRowCount: 0,
        message: 'xls/xlsx만 지원합니다.',
      });
      continue;
    }

    try {
      const templateResult = await extractTemplateFile(file, referenceYearMonth);
      if (templateResult && templateResult.rows.length > 0) {
        const mapped: CommissionParseRow[] = templateResult.rows.map((row) => ({
          referenceYearMonth: row.referenceYearMonth,
          pharmaceuticalCompanyName: row.pharmaceuticalCompanyName,
          drugCode: row.drugCode,
          drugName: row.drugName,
          price: row.price,
          standardCommissionRate: row.standardCommissionRate,
          commissionNote: row.commissionNote,
          sourceFile: file.name,
        }));
        fileStatuses.push({
          fileName: file.name,
          status: 'parsed',
          extractedRowCount: mapped.length,
          message: `템플릿: ${templateResult.templateId}`,
        });
        rows.push(...mapped);
        continue;
      }

      const buffer = await file.arrayBuffer();
      const workbook = XLSX.read(buffer, { type: 'array', cellDates: false });

      const driveParsed = parseDriveWorkbook(workbook);
      if (driveParsed && driveParsed.rows.length > 0) {
        const mapped = driveParsed.rows.map((driveRow) => {
          const preview = driveRowToPreview(driveRow);
          return {
            ...preview,
            referenceYearMonth: preview.referenceYearMonth || referenceYearMonth,
            sourceFile: file.name,
          };
        });
        fileStatuses.push({
          fileName: file.name,
          status: 'parsed',
          extractedRowCount: mapped.length,
          message: '드라이브 포맷',
        });
        rows.push(...mapped);
        continue;
      }

      const firstSheet = workbook.SheetNames[0];
      const sheetRows = XLSX.utils.sheet_to_json<unknown[]>(
        workbook.Sheets[firstSheet],
        { header: 1, defval: null, blankrows: false },
      );
      const extracted = extractRows(sheetRows, file.name, referenceYearMonth);

      if (extracted.length === 0) {
        fileStatuses.push({
          fileName: file.name,
          status: 'unmatched',
          extractedRowCount: 0,
          message: '인식 가능한 헤더를 찾지 못했습니다.',
        });
      } else {
        fileStatuses.push({
          fileName: file.name,
          status: 'parsed',
          extractedRowCount: extracted.length,
        });
        rows.push(...extracted);
      }
    } catch {
      fileStatuses.push({
        fileName: file.name,
        status: 'error',
        extractedRowCount: 0,
        message: '파일 파싱에 실패했습니다.',
      });
    }
  }

  const pharmaSet = new Set(
    rows.map((r) => r.pharmaceuticalCompanyName).filter(Boolean),
  );

  return {
    summary: {
      totalFiles: sorted.length,
      parsedFiles: fileStatuses.filter((f) => f.status === 'parsed').length,
      extractedRows: rows.length,
      pharmaceuticalCompanies: pharmaSet.size,
    },
    rows,
    files: fileStatuses,
  };
};

export const COMMISSION_EXPORT_HEADER_MAP: Record<string, string> = {
  referenceYearMonth: 'reference_year_month',
  drugCode: 'drug_code',
  drugName: 'drug_name',
  pharmaceuticalCompanyName: 'pharma_company_name',
  price: 'price',
  standardCommissionRate: 'standard_commission_rate',
  commissionNote: 'standard_commission_note',
  sourceFile: 'source_file',
};
